import React, { useState, useEffect, useCallback } from 'react';
import { Box, IconButton, Slider, Typography, Paper, Stack, Button, Modal, Backdrop } from '@mui/material';
import CropIcon from '@mui/icons-material/Crop';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';

const objectFitOptions = [
  { value: 'cover', label: 'Cover' },
  { value: 'contain', label: 'Contain' },
  { value: 'fill', label: 'Fill' },
  { value: 'none', label: 'None' },
];

const getStorageKey = (tableName, recordId, imageField, src) => {
  if (!src) return `crop_${tableName}_${recordId}_${imageField}_nosrc`;
  
  // Create a more robust hash of the URL to avoid collisions
  // We use the full URL and hash it to a manageable length
  let hash = 0;
  for (let i = 0; i < src.length; i++) {
    const char = src.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  const safeHash = Math.abs(hash).toString(36);
  
  return `crop_${tableName}_${recordId}_${imageField}_${safeHash}`;
};

const EditableImage = ({ 
  src, 
  alt = '', 
  sx = {}, 
  imgSx = {},
  tableName,
  recordId,
  imageField = 'cover_image',
  initialObjectFit = 'cover',
  initialObjectPosition = '50% 50%',
  onUpdate,
}) => {
  const { isAdmin, isAuthenticated } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const storageKey = tableName && recordId ? getStorageKey(tableName, recordId, imageField, src) : null;
  
  const [objectFit, setObjectFit] = useState(initialObjectFit);
  const [positionX, setPositionX] = useState(parseInt(initialObjectPosition.split(' ')[0]) || 50);
  const [positionY, setPositionY] = useState(parseInt(initialObjectPosition.split(' ')[1]) || 50);
  const [saving, setSaving] = useState(false);

  const loadFromLocalStorage = useCallback(() => {
    if (!storageKey) return null;
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  }, [storageKey]);

  const loadFromSupabase = useCallback(async () => {
    if (!storageKey) return null;
    try {
      const { data, error } = await supabase
        .from('image_crop_settings')
        .select('*')
        .eq('storage_key', storageKey)
        .single();
      
      if (error || !data) return null;
      return {
        objectFit: data.object_fit,
        positionX: data.position_x,
        positionY: data.position_y,
        objectPosition: data.object_position
      };
    } catch {
      return null;
    }
  }, [storageKey]);

  const loadFromJson = useCallback(async () => {
    if (!storageKey) return null;
    try {
      const response = await fetch('/data/image-crop-settings.json');
      if (!response.ok) return null;
      const allSettings = await response.json();
      return allSettings[storageKey] || null;
    } catch {
      return null;
    }
  }, [storageKey]);

  const saveToLocalStorage = useCallback((settings) => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(settings));
    } catch {
      console.warn('Failed to save to localStorage');
    }
  }, [storageKey]);

  const saveToSupabase = useCallback(async (settings) => {
    if (!storageKey) return;
    try {
      const { error } = await supabase
        .from('image_crop_settings')
        .upsert({
          storage_key: storageKey,
          object_fit: settings.objectFit,
          position_x: settings.positionX,
          position_y: settings.positionY,
          object_position: settings.objectPosition,
          updated_at: new Date().toISOString()
        }, { onConflict: 'storage_key' });
      
      if (error) {
        console.warn('Failed to save to Supabase:', error.message);
      }
    } catch (err) {
      console.warn('Failed to save to Supabase:', err);
    }
  }, [storageKey]);

  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true);
      
      const localSettings = loadFromLocalStorage();
      if (localSettings) {
        setObjectFit(localSettings.objectFit || initialObjectFit);
        setPositionX(localSettings.positionX ?? 50);
        setPositionY(localSettings.positionY ?? 50);
        setIsLoading(false);
        return;
      }
      
      const supabaseSettings = await loadFromSupabase();
      if (supabaseSettings) {
        setObjectFit(supabaseSettings.objectFit || initialObjectFit);
        setPositionX(supabaseSettings.positionX ?? 50);
        setPositionY(supabaseSettings.positionY ?? 50);
        saveToLocalStorage(supabaseSettings);
        setIsLoading(false);
        return;
      }
      
      const jsonSettings = await loadFromJson();
      if (jsonSettings) {
        setObjectFit(jsonSettings.objectFit || initialObjectFit);
        setPositionX(jsonSettings.positionX ?? 50);
        setPositionY(jsonSettings.positionY ?? 50);
        saveToLocalStorage(jsonSettings);
        setIsLoading(false);
        return;
      }
      
      setObjectFit(initialObjectFit);
      const [x, y] = initialObjectPosition.split(' ');
      setPositionX(parseInt(x) || 50);
      setPositionY(parseInt(y) || 50);
      
      setIsLoading(false);
    };
    
    loadSettings();
  }, [storageKey, initialObjectFit, initialObjectPosition, loadFromLocalStorage, loadFromSupabase, loadFromJson, saveToLocalStorage]);

  const showEditButton = isAuthenticated && isAdmin;

  const handleSave = async () => {
    try {
      setSaving(true);
      const objectPosition = `${positionX}% ${positionY}%`;
      
      const settings = {
        objectFit,
        positionX,
        positionY,
        objectPosition
      };
      
      saveToLocalStorage(settings);
      
      await saveToSupabase(settings);
      
      if (onUpdate) {
        await onUpdate(settings);
      }
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving image settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    const saved = loadFromLocalStorage();
    if (saved) {
      setObjectFit(saved.objectFit || initialObjectFit);
      setPositionX(saved.positionX ?? 50);
      setPositionY(saved.positionY ?? 50);
    } else {
      setObjectFit(initialObjectFit);
      setPositionX(parseInt(initialObjectPosition.split(' ')[0]) || 50);
      setPositionY(parseInt(initialObjectPosition.split(' ')[1]) || 50);
    }
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <Box sx={{ position: 'relative', ...sx }}>
        <Box 
          sx={{ 
            width: '100%', 
            height: '100%', 
            bgcolor: 'rgba(0,0,0,0.1)',
            ...imgSx
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', ...sx }}>
      <Box 
        component="img"
        src={src}
        alt={alt}
        sx={{ 
          width: '100%', 
          height: '100%', 
          objectFit: objectFit,
          objectPosition: `${positionX}% ${positionY}%`,
          transition: 'all 0.3s ease',
          ...(isEditing && { outline: '3px solid #D4AF37', outlineOffset: '-3px' }),
          ...imgSx
        }}
      />
      
      {showEditButton && !isEditing && (
        <IconButton
          onClick={() => setIsEditing(true)}
          sx={{ 
            position: 'absolute', 
            top: 10, 
            left: 10, 
            bgcolor: 'rgba(0,0,0,0.7)', 
            color: '#D4AF37',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.9)' },
            zIndex: 10
          }}
          size="small"
        >
          <CropIcon fontSize="small" />
        </IconButton>
      )}

      <Modal
        open={isEditing}
        onClose={handleCancel}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: 'rgba(0,0,0,0.3)' }
          }
        }}
      >
        <Paper
          sx={{
            position: 'fixed',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: '95%', sm: '500px' },
            maxWidth: '600px',
            bgcolor: 'rgba(31, 41, 55, 0.98)',
            backdropFilter: 'blur(10px)',
            p: 3,
            borderRadius: '16px',
            border: '1px solid rgba(212, 175, 55, 0.3)',
            boxShadow: '0 -10px 40px rgba(0,0,0,0.5)'
          }}
        >
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ color: '#D4AF37', fontWeight: 700 }}>
                Image Crop Settings
              </Typography>
              <IconButton size="small" onClick={handleCancel} sx={{ color: '#9CA3AF' }}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box>
              <Typography variant="body2" sx={{ color: '#9CA3AF', mb: 1.5 }}>
                Fit Mode
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {objectFitOptions.map((option) => (
                  <Button
                    key={option.value}
                    size="medium"
                    variant={objectFit === option.value ? 'contained' : 'outlined'}
                    onClick={() => setObjectFit(option.value)}
                    sx={{
                      minWidth: '70px',
                      px: 2,
                      py: 1,
                      bgcolor: objectFit === option.value ? '#0FA3A3' : 'transparent',
                      borderColor: '#0FA3A3',
                      color: objectFit === option.value ? 'white' : '#0FA3A3',
                      '&:hover': { 
                        bgcolor: objectFit === option.value ? '#0C8A8A' : 'rgba(15,163,163,0.1)',
                        borderColor: '#0FA3A3'
                      }
                    }}
                  >
                    {option.label}
                  </Button>
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography variant="body2" sx={{ color: '#9CA3AF', mb: 1 }}>
                Horizontal Position: <span style={{ color: '#0FA3A3', fontWeight: 700 }}>{positionX}%</span>
              </Typography>
              <Slider
                value={positionX}
                onChange={(e, val) => setPositionX(val)}
                min={0}
                max={100}
                sx={{ color: '#0FA3A3' }}
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ color: '#9CA3AF', mb: 1 }}>
                Vertical Position: <span style={{ color: '#0FA3A3', fontWeight: 700 }}>{positionY}%</span>
              </Typography>
              <Slider
                value={positionY}
                onChange={(e, val) => setPositionY(val)}
                min={0}
                max={100}
                sx={{ color: '#0FA3A3' }}
              />
            </Box>

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{ 
                  flex: 1,
                  py: 1.5,
                  borderRadius: '10px',
                  color: '#9CA3AF',
                  borderColor: '#9CA3AF',
                  fontWeight: 600,
                  '&:hover': { borderColor: '#fff', color: '#fff' }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{ 
                  flex: 1,
                  py: 1.5,
                  borderRadius: '10px',
                  bgcolor: '#D4AF37', 
                  color: '#0B0F14',
                  fontWeight: 700,
                  '&:hover': { bgcolor: '#B89730' }
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Modal>
    </Box>
  );
};

export default EditableImage;
